import { Trans, t } from "@lingui/macro";
import cx from "classnames";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Address } from "viem";

import { getChainSlug, getExplorerUrl } from "config/chains";
import { useMarketsInfoData } from "context/SyntheticsStateContext/hooks/globalsHooks";
import { selectChainId } from "context/SyntheticsStateContext/selectors/globalSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { isDecreaseOrderType, isLiquidationOrderType, isSwapOrderType } from "domain/synthetics/orders";
import {
  isPositionTradeAction,
  PositionTradeAction,
  SwapTradeAction,
  TradeAction,
  TradeActionType,
} from "domain/synthetics/tradeHistory";
import { useCloseSettlement } from "domain/synthetics/tradeHistory/useCloseSettlement";
import { EMPTY_ARRAY } from "lib/objects";
import { userAnalytics } from "lib/userAnalytics";
import { SharePositionClickEvent } from "lib/userAnalytics/types";
import useWallet from "lib/wallets/useWallet";
import { buildAccountDashboardUrl } from "pages/AccountDashboard/buildAccountDashboardUrl";

import Button from "components/Button/Button";
import ExternalLink from "components/ExternalLink/ExternalLink";
import { MarketWithDirectionLabel } from "components/MarketWithDirectionLabel/MarketWithDirectionLabel";
import StatsTooltipRow from "components/StatsTooltip/StatsTooltipRow";
import { SwapMarketLabel } from "components/SwapMarketLabel/SwapMarketLabel";
import { TableTd, TableTr } from "components/Table/Table";
import TokenIcon from "components/TokenIcon/TokenIcon";
import TooltipWithPortal from "components/Tooltip/TooltipWithPortal";

import FilterHistoryIcon from "img/ic_filter_history.svg?react";
import NewLinkIconThin from "img/ic_new_link_thin.svg?react";
import SpinnerIcon from "img/ic_spinner.svg?react";

import ShareClosedPosition from "./ShareClosedPosition";
import { formatPositionMessage, getSettlementTooltipLines } from "./utils/position";
import { TooltipContent, TooltipString } from "./utils/shared";
import { formatSwapMessage } from "./utils/swap";

import "./TradeHistoryRow.scss";

type Props = {
  tradeAction: TradeAction;
  minCollateralUsd: bigint;
  shouldDisplayAccount?: boolean;
  showDebugValues?: boolean;
  onSelectPositionLifecycle?: (tradeAction: PositionTradeAction) => void;
};

function LineSpan({ span }: { span: TooltipString }) {
  if (span === undefined) {
    return null;
  }

  if (typeof span === "string") {
    return span;
  }

  return (
    <span
      className={cx({
        "text-red-500": span.state === "error",
        "text-green-500": span.state === "success",
        muted: span.state === "muted",
      })}
    >
      {span.text}
    </span>
  );
}

function LineSpans({ spans }: { spans: TooltipString[] }) {
  return (
    <>
      {spans.map((span, i) => (
        <LineSpan key={i} span={span} />
      ))}
    </>
  );
}

function TooltipContentComponent({ content }: { content: TooltipContent }) {
  return (
    <div className="TradeHistoryRow-tooltip">
      {content.map((line, i) => {
        if (line === undefined) {
          return null;
        }

        if (line === "") {
          return <br key={i} />;
        }

        if (typeof line === "string") {
          return <div key={i}>{line}</div>;
        }

        if (Array.isArray(line)) {
          return (
            <div key={i}>
              <LineSpans spans={line} />
            </div>
          );
        }

        if ("key" in line && "value" in line) {
          return (
            <StatsTooltipRow
              key={i}
              label={line.key}
              value={Array.isArray(line.value) ? <LineSpans spans={line.value} /> : <LineSpan span={line.value} />}
              showDollar={false}
            />
          );
        }

        return (
          <div key={i}>
            <LineSpan span={line} />
          </div>
        );
      })}
    </div>
  );
}

function getFullCloseCandidate(tradeAction: TradeAction): PositionTradeAction | undefined {
  if (!isPositionTradeAction(tradeAction)) {
    return undefined;
  }

  const isFullCloseCandidate =
    tradeAction.eventName === TradeActionType.OrderExecuted &&
    (isDecreaseOrderType(tradeAction.orderType) || isLiquidationOrderType(tradeAction.orderType)) &&
    tradeAction.sizeDeltaUsd > 0n;

  return isFullCloseCandidate ? tradeAction : undefined;
}

function FeesTooltipContent({ tradeAction, feesLines }: { tradeAction: TradeAction; feesLines: TooltipContent }) {
  const chainId = useSelector(selectChainId);

  const fullCloseCandidate = getFullCloseCandidate(tradeAction);

  const { settlement, isLoading } = useCloseSettlement(chainId, fullCloseCandidate);

  const content = useMemo(() => {
    if (!fullCloseCandidate || !settlement?.isFullClose || !settlement.closeChange) {
      return feesLines;
    }

    const settlementLines = getSettlementTooltipLines(fullCloseCandidate, settlement.closeChange, settlement.openChange);

    if (feesLines.length === 0) {
      return settlementLines[0] === "" ? settlementLines.slice(1) : settlementLines;
    }

    return [...feesLines, ...settlementLines];
  }, [feesLines, fullCloseCandidate, settlement]);

  return (
    <>
      <TooltipContentComponent content={content} />
      {isLoading ? (
        <div className="mt-8">
          <SpinnerIcon className="size-12 animate-spin" />
        </div>
      ) : null}
    </>
  );
}

const PRICE_TOOLTIP_WIDTH = 400;

export function TradeHistoryRow({
  minCollateralUsd,
  tradeAction,
  shouldDisplayAccount,
  showDebugValues,
  onSelectPositionLifecycle,
}: Props) {
  const chainId = useSelector(selectChainId);
  const { account } = useWallet();
  const marketsInfoData = useMarketsInfoData();

  const msg = useMemo(() => {
    if (isSwapOrderType(tradeAction.orderType!)) {
      return formatSwapMessage(tradeAction as SwapTradeAction, marketsInfoData);
    }

    return formatPositionMessage(tradeAction as PositionTradeAction, minCollateralUsd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSwapOrderType(tradeAction.orderType!) && marketsInfoData, minCollateralUsd.toString(), tradeAction.id]);

  const renderTimestamp = useCallback(() => msg.timestampUTC, [msg.timestampUTC]);

  const renderMarketContent = useCallback(() => {
    if (msg.indexName) {
      return (
        <StatsTooltipRow
          label={t`Market`}
          value={
            <div className="flex items-center">
              <span>{msg.indexName!}</span>
              <span className="subtext leading-1">[{msg.poolName!}]</span>
            </div>
          }
          showDollar={false}
        />
      );
    }

    return (
      <>
        {msg.fullMarketNames?.map((market, index) => (
          <span key={market.indexName}>
            {index > 0 && " → "}
            <span>{market.indexName}</span>
            <span className="subtext leading-1">[{market.poolName}]</span>
          </span>
        ))}
      </>
    );
  }, [msg.fullMarketNames, msg.indexName, msg.poolName]);

  const renderPriceContent = useCallback(
    () => <TooltipContentComponent content={msg.priceComment ?? EMPTY_ARRAY} />,
    [msg.priceComment]
  );

  const renderSizeContent = useCallback(
    () => <TooltipContentComponent content={msg.sizeComment ?? EMPTY_ARRAY} />,
    [msg.sizeComment]
  );

  const renderActionTooltipContent = useCallback(
    () => <TooltipContentComponent content={msg.actionComment!} />,
    [msg.actionComment]
  );

  const renderFeesTooltipContent = useCallback(
    () => <FeesTooltipContent tradeAction={tradeAction} feesLines={msg.feesTooltip ?? EMPTY_ARRAY} />,
    [msg.feesTooltip, tradeAction]
  );

  const marketTooltipHandle = useMemo(
    () =>
      msg.swapFromTokenSymbol ? (
        <SwapMarketLabel bordered fromSymbol={msg.swapFromTokenSymbol!} toSymbol={msg.swapToTokenSymbol!} />
      ) : (
        <div className="cursor-help">
          <MarketWithDirectionLabel
            bordered
            indexName={msg.indexName!}
            isLong={msg.isLong!}
            tokenSymbol={msg.indexTokenSymbol!}
          />
        </div>
      ),
    [msg.indexName, msg.indexTokenSymbol, msg.isLong, msg.swapFromTokenSymbol, msg.swapToTokenSymbol]
  );

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const handleShareClick = useCallback(() => {
    userAnalytics.pushEvent<SharePositionClickEvent>({
      event: "SharePositionAction",
      data: {
        action: "SharePositionClick",
      },
    });
    setIsShareModalOpen(true);
  }, [setIsShareModalOpen]);

  const shouldDisplayShareButton =
    isDecreaseOrderType(tradeAction.orderType) &&
    tradeAction.eventName === TradeActionType.OrderExecuted &&
    account === tradeAction.account;

  const isFullCloseSettlementCandidate = getFullCloseCandidate(tradeAction) !== undefined;

  const shouldDisplayPositionLifecycleButton =
    isPositionTradeAction(tradeAction) &&
    Boolean(tradeAction.positionLifecycleId) &&
    Boolean(onSelectPositionLifecycle);

  const handleSelectPositionLifecycleClick = useCallback(() => {
    if (isPositionTradeAction(tradeAction)) {
      onSelectPositionLifecycle?.(tradeAction);
    }
  }, [onSelectPositionLifecycle, tradeAction]);

  return (
    <>
      <TableTr
        hoverable={true}
        className={cx("TradeHistoryRow", {
          debug: showDebugValues,
        })}
      >
        <TableTd>
          <div className="flex">
            <div className="flex items-center gap-4">
              {msg.actionComment ? (
                <TooltipWithPortal
                  className={cx("TradeHistoryRow-action-handle")}
                  handleClassName={cx("TradeHistoryRow-action-handle", {
                    "text-red-500 !decoration-red-500/50": msg.isActionError,
                  })}
                  handle={<span className="font-medium">{msg.action}</span>}
                  renderContent={renderActionTooltipContent}
                />
              ) : (
                <span
                  className={cx("TradeHistoryRow-action-handle font-medium", {
                    "text-red-500": msg.isActionError,
                  })}
                >
                  {msg.action}
                </span>
              )}
              <ExternalLink
                className="TradeHistoryRow-external-link size-12"
                href={`${getExplorerUrl(chainId)}tx/${tradeAction.transactionHash}`}
              >
                <NewLinkIconThin />
              </ExternalLink>
            </div>
            <div className="flex flex-row items-center">
              {showDebugValues && (
                <Link
                  to={`/parsetx/${getChainSlug(chainId)}/${tradeAction.transactionHash}`}
                  className="text-body-small ml-5 text-typography-secondary hover:text-typography-primary"
                >
                  <Trans>Events</Trans>
                </Link>
              )}
            </div>
          </div>
          <TooltipWithPortal
            variant="none"
            handle={<span className="TradeHistoryRow-time muted cursor-help">{msg.timestamp}</span>}
            tooltipClassName="TradeHistoryRow-tooltip-portal cursor-help *:cursor-auto"
            renderContent={renderTimestamp}
          />
          {shouldDisplayAccount && (
            <Link
              className="TradeHistoryRow-account muted underline"
              to={buildAccountDashboardUrl(tradeAction.account as Address, undefined, 2)}
            >
              {tradeAction.account}
            </Link>
          )}
        </TableTd>
        <TableTd>
          <TooltipWithPortal
            variant="none"
            tooltipClassName="cursor-help *:cursor-auto"
            handle={marketTooltipHandle}
            renderContent={renderMarketContent}
          />
        </TableTd>
        <TableTd>
          {msg.swapFromTokenSymbol ? (
            <span className="numbers">
              <Trans>
                {msg.swapFromTokenAmount} <TokenIcon symbol={msg.swapFromTokenSymbol!} displaySize={18} />
                <span> to </span>
                {msg.swapToTokenAmount} <TokenIcon symbol={msg.swapToTokenSymbol!} displaySize={18} />
              </Trans>
            </span>
          ) : msg.sizeComment ? (
            <TooltipWithPortal
              variant="none"
              handle={<span className="numbers">{msg.size}</span>}
              renderContent={renderSizeContent}
            />
          ) : (
            <span className="numbers">{msg.size}</span>
          )}
        </TableTd>
        <TableTd>
          {msg.priceComment ? (
            <TooltipWithPortal
              tooltipClassName="TradeHistoryRow-price-tooltip-portal"
              handle={msg.price}
              handleClassName="numbers"
              position="bottom-end"
              renderContent={renderPriceContent}
              maxAllowedWidth={PRICE_TOOLTIP_WIDTH}
            />
          ) : (
            <span className="numbers">{msg.price}</span>
          )}
        </TableTd>
        <TableTd>
          {!msg.pnl ? (
            <span className="text-typography-secondary">-</span>
          ) : msg.pnlTooltip ? (
            <TooltipWithPortal
              handle={
                <span
                  className={cx("numbers", {
                    "text-red-500": msg.pnlState === "error",
                    "text-green-500": msg.pnlState === "success",
                  })}
                >
                  {msg.pnl}
                </span>
              }
              content={msg.pnlTooltip}
            />
          ) : (
            <span
              className={cx("numbers", {
                "text-red-500": msg.pnlState === "error",
                "text-green-500": msg.pnlState === "success",
              })}
            >
              {msg.pnl}
            </span>
          )}
        </TableTd>
        <TableTd>
          {(msg.feesTooltip && msg.feesTooltip.length > 0) || isFullCloseSettlementCandidate ? (
            <TooltipWithPortal
              handle={
                msg.fees ? (
                  <span className="numbers">{msg.fees}</span>
                ) : (
                  <span className="text-typography-secondary">-</span>
                )
              }
              renderContent={renderFeesTooltipContent}
            />
          ) : !msg.fees ? (
            <span className="text-typography-secondary">-</span>
          ) : (
            <span className="numbers">{msg.fees}</span>
          )}
        </TableTd>
        <TableTd>
          <div className="flex items-center justify-end gap-4">
            {shouldDisplayShareButton ? (
              <Button variant="ghost" onClick={handleShareClick}>
                <NewLinkIconThin className="size-16" />
                <Trans>Share</Trans>
              </Button>
            ) : null}
            {shouldDisplayPositionLifecycleButton ? (
              <TooltipWithPortal
                variant="none"
                position="bottom-end"
                shouldPreventDefault={false}
                handle={
                  <Button
                    variant="ghost"
                    onClick={handleSelectPositionLifecycleClick}
                    className="!min-h-28 !px-8 !py-6"
                    aria-label={t`View position history`}
                  >
                    <FilterHistoryIcon className="size-14" />
                  </Button>
                }
                content={<Trans>View position history</Trans>}
              />
            ) : (
              // Reserve the button's space so the Share button and layout don't shift between rows/states.
              <Button variant="ghost" className="invisible !min-h-28 !px-8 !py-6">
                <FilterHistoryIcon className="size-14" />
              </Button>
            )}
          </div>
        </TableTd>
      </TableTr>
      {isPositionTradeAction(tradeAction) ? (
        <ShareClosedPosition
          tradeAction={tradeAction}
          isShareModalOpen={isShareModalOpen}
          setIsShareModalOpen={setIsShareModalOpen}
          shareSource="trade-history-list"
        />
      ) : null}
      {showDebugValues && (
        <TableTr>
          <TableTd colSpan={42}>
            <div className="muted">
              <Trans>Order key:</Trans> {tradeAction.orderKey}
            </div>
          </TableTd>
        </TableTr>
      )}
    </>
  );
}
